import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import '@chatui/core/es/styles/index.less';
import Chat, {
  Bubble,
  MessageProps,
  useMessages,
  Icon,
  Divider,
  Input,
} from '@chatui/core';
import { InputType } from '@chatui/core/lib/components/Composer';
import type { postDataType } from '../../services/api';
import '@chatui/core/dist/index.css';
import './style.css';
import {
  abortFetchResponse,
  authenticate,
  fetchBotResponse,
  sendFeedback,
} from '../../services/api';
import TalkingHead from '../TalkingHead';
import { GridLoader } from '../Loader';
import useRecorder from '../../hooks/useRecorder';
import VoiceComposer from '../VoiceComposer';
import AudioMessage from '../AudioMessage';
import { ChatStatusType, MessageType } from '../../utils/types';
import {
  BOT_AVATAR_DEFAULT,
  DEFAULT_MESSAGE,
  START_MSG_ID,
  END_MSG_ID,
} from '../../utils/constants';
import { nanoid } from 'nanoid';
import TranslationBubble from '../TranslationBubble';
import {
  InitialLoadingMessage,
  InitialErrorMessage,
} from '../Card/InitialMessage';
import ActionCard, { ButtonProps } from '../Card/ActionCard';
import ResponseContainer from '../Card/ResponseContainer';
import MultimediaResponseCard from '../Card/MultimediaResponseCard';
import TextMessage from '../Card/TextMessage';
import { MessageId } from '@chatui/core/lib/components/Message';
import useDebounce from '../../hooks/useDebounce';
import { trackEvent } from '../../utils/helper';
const mic = require('../../assets/mic.png');
const allowmic = require('../../assets/allow.png');

interface BotContainerProps {
  dialogId: string;
  isMobile: boolean;
  collapsed?: boolean;
  params?: any;
}

const BotContainer = (props: BotContainerProps) => {
  const { dialogId, isMobile, collapsed, params } = props;
  const { messages, appendMsg, setTyping, deleteMsg, updateMsg } = useMessages(
    []
  );
  const [videoSrc, setVideoSrc] = useState<string>('');
  const [idleVideoSrc, setIdleVideoSrc] = useState<string>('');
  const [inputType, setInputType] = useState<InputType>('text');
  const playerRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [chatStatus, setChatStatus] = useState<ChatStatusType>('beforeStart');
  const [botData, setBotData] = useState<any>({});
  const [idToken, setIdToken] = useState<string>('');
  const [sessionAttr, setSessionAttr] = useState<string>('{}');
  const [sessionId, setSessionId] = useState<string>('');
  const [botAvatar, setBotAvatar] = useState<string>(BOT_AVATAR_DEFAULT);
  const [hasTalkingHead, setHasTalkingHead] = useState<boolean>(false);
  const [wakeupMsg, setWakeupMsg] = useState<string>('');
  const [audioSrc, setAudioSrc] = useState<string>('');
  const [lipsyncVersion, setLipsyncVersion] = useState<number | undefined>(
    undefined
  );
  const [fillers, setFillers] = useState<string[]>([]);
  const [isFillerPlaying, setIsFillerPlaying] = useState<boolean>(false);
  const [filterPlayed, setFilterPlayed] = useState<number>(0);
  const [talkingHeadOptOn, setTalkingHeadOptOn] = useState<boolean>(false);
  const [authLoading, setAuthLoading] = useState<boolean>(false);
  const [talkingHeadMuted, setTalkingHeadMuted] = useState<boolean>(false);
  const [contactCollectionOptOn, setContactCollectionOptOn] =
    useState<boolean>(false);
  const [contactCollectionTitle, setContactCollectionTitle] =
    useState<string>('');
  const [contactName, setContactName] = useState<string>('');
  const [contactEmail, setContactEmail] = useState<string>('');
  const [contactMobile, setContactMobile] = useState<string>('');
  const [quickQuestionList, setQuickQuestionList] = useState<any[]>([]);
  const [currentAudioPlayingId, setCurrentAudioPlayingId] = useState<
    string | MessageId
  >('');
  const [theme, setTheme] = useState<string>('');
  const [apiVersion, setApiVersion] = useState<string>('');

  const { startRecording, stopRecording, recorder, silenceDetected } =
    useRecorder();

  const idle = useCallback(() => {
    setVideoSrc(idleVideoSrc);
    setAudioSrc('');
    setChatStatus('idle');
  }, [idleVideoSrc]);

  const stopVoice = useCallback(
    (keepTalkingHeadPlaying?: boolean) => {
      if (recorder && recorder.state === 'recording') {
        recorder.stop();
      }
      setInputType('text');
      if (keepTalkingHeadPlaying) {
        return;
      }
      idle();
      abortFetchResponse();
      setTyping(false);
    },
    [idle, recorder, setTyping]
  );

  useEffect(() => {
    if (talkingHeadOptOn && !isMobile) {
      if (collapsed) {
        setHasTalkingHead(false);
        if (chatStatus !== 'beforeStart') {
          stopVoice();
        }
      } else {
        setHasTalkingHead(true);
      }
    }
  }, [collapsed, talkingHeadOptOn, isMobile, chatStatus, stopVoice]);

  const resetState = useCallback(() => {
    setInputType('text');
    setBotData({});
    setIdToken('');
    setSessionAttr('{}');
    setSessionId('');
  }, []);

  const recordVoice = useCallback(async () => {
    const recorderResponse = await startRecording();
    if (!recorderResponse.started) {
      appendMsg({
        type: recorderResponse.errorType || 'recorderError',
        content: {
          text: recorderResponse.error,
        },
        position: 'center',
      });
      setInputType('text');
      setChatStatus('idle');
      return;
    }
    setChatStatus('listening');
  }, [appendMsg, startRecording]);

  const playFiller = useCallback(() => {
    setIsFillerPlaying(true);
    setVideoSrc(fillers[Math.floor(Math.random() * fillers.length)]);
    setFilterPlayed(filterPlayed + 1);
  }, [fillers, filterPlayed]);

  const debouncedPlayFiller = useDebounce(playFiller, 250);
  const debounceRecordVoice = useDebounce(recordVoice, 100);

  const keepRecording = useCallback(() => {
    if (inputType === 'voice') {
      debounceRecordVoice();
    }
  }, [debounceRecordVoice, inputType]);

  const postMessage = useCallback(
    async (
      type: string,
      val: any,
      filterOff?: boolean,
      newSession?: boolean,
      newToken?: string
    ) => {
      setTyping(true);
      setChatStatus('processing');
      if (
        hasTalkingHead &&
        chatStatus !== 'beforeStart' &&
        fillers?.length > 0 &&
        filterPlayed < 3 &&
        !filterOff
      ) {
        debouncedPlayFiller();
      }

      const { voice, bot_id, bot_alias_id, locale_id } = botData;
      let sessionAttrMerge = {
        ...params,
      };
      if (contactCollectionOptOn) {
        sessionAttrMerge = {
          ...sessionAttrMerge,
          contactName,
          contactEmail,
          contactMobile,
        };
      }
      let ssid = sessionId;
      let token = idToken;
      if (newSession) {
        ssid = nanoid();
        setSessionId(ssid);
        token = newToken || idToken;
      } else {
        sessionAttrMerge = { ...sessionAttrMerge, ...JSON.parse(sessionAttr) };
      }

      let postData: postDataType = {
        dialog_id: dialogId,
        idle_sync_url: idleVideoSrc,
        voice,
        bot_id,
        bot_alias_id,
        locale_id,
        session_id: ssid,
        session_attributes: JSON.stringify(sessionAttrMerge),
        talking_head: hasTalkingHead,
        isMobile,
      };

      if (lipsyncVersion !== undefined) {
        postData = { ...postData, lipsync_version: lipsyncVersion };
      }

      try {
        const result = await fetchBotResponse(
          postData,
          token,
          val,
          type,
          apiVersion
        );

        const {
          bot_response,
          bot_response_media,
          video,
          audio,
          session_attributes,
          user_text,
          session_state,
          statusCode,
        } = result;

        if (statusCode === '500') {
          appendMsg({
            type: 'responseError',
            content: { text: DEFAULT_MESSAGE.noResponseErr },
            position: 'left',
            user: { avatar: botAvatar },
          });

          stopVoice();
          return;
        }

        if (session_state === 'Close') {
          appendMsg({
            type: 'end',
            content: {
              text: DEFAULT_MESSAGE.chatEnd,
              title: DEFAULT_MESSAGE.sessionClosedTitle,
            },
            position: 'center',
            _id: END_MSG_ID,
          });
          stopVoice(true);
          setChatStatus('end');
          return;
        }

        if (type === 'voice' && user_text) {
          appendMsg({
            type: 'translation',
            content: { text: user_text },
            position: 'right',
          });
        }

        let hasResponse = false;
        let call_id = '';
        let responseContent = {};

        if (bot_response) {
          hasResponse = true;
          responseContent = { text: bot_response.replace(/^\n+/g, '').trim() };
        }

        if (bot_response_media) {
          try {
            // const multiMediaResponse = JSON.parse(bot_response_media);
            // responseContent = { ...responseContent, multiMediaResponse };
            responseContent = {
              ...responseContent,
              multiMediaResponse: bot_response_media,
            };
            hasResponse = true;
          } catch (e) {
            console.log(e);
          }
        }

        if (hasTalkingHead) {
          if (video && !video.startsWith('http')) {
            setVideoSrc('data:video/mp4;base64,' + video);
            try {
              playerRef.current?.play();
              setChatStatus('responding');
            } catch (e) {
              console.log(e);
              idle();
              keepRecording();
            }
          } else {
            idle();
            keepRecording();
          }
          setIsFillerPlaying(false);
        } else if (audio) {
          //when no talking head
          if (isMobile) {
            //on mobile, stop voice after response
            //add audio to response content
            responseContent = {
              ...responseContent,
              audio: `data:audio/mp3;base64,${audio}`,
            };
            stopVoice();
          } else if (type === 'voice') {
            //on desktop, play audio after response
            setAudioSrc(`data:audio/mp3;base64,${audio}`);
            setChatStatus('responding');
          }
          // audioRef.current?.play();
        } else {
          stopVoice();
        }

        if (hasResponse) {
          setSessionAttr(session_attributes);
          const sessionAttrObj = JSON.parse(session_attributes);
          if (sessionAttrObj.call_id) {
            call_id = sessionAttrObj.call_id;
            responseContent = { ...responseContent, call_id };
          }
          appendMsg({
            type: 'response',
            content: responseContent,
            position: 'left',
            user: { avatar: botAvatar },
            _id: nanoid(),
          });

          if (session_state === 'Complete') {
            appendMsg({
              type: 'end',
              content: {
                text: DEFAULT_MESSAGE.complete,
                title: DEFAULT_MESSAGE.sessionCompletedTitle,
              },
              position: 'center',
              _id: END_MSG_ID,
            });
            stopVoice(true);
            setChatStatus('end');
            return;
          }
        } else {
          appendMsg({
            type: 'responseError',
            content: { text: DEFAULT_MESSAGE.noResponseErr },
            position: 'left',
            user: { avatar: botAvatar },
          });
          stopVoice();
        }
      } catch (e: any) {
        console.log(e);
        if (e.name === 'CanceledError') return;

        appendMsg({
          type: 'responseError',
          content: { text: DEFAULT_MESSAGE.noResponseErr },
          position: 'left',
          user: { avatar: botAvatar },
        });

        stopVoice();

        //TODO: handle different error codes
        //TODO: network errors need to have response for UI to check error status code
        //status code:
        //401 Unauthorized
        //403 Forbidden
        //404 Not Found
        //504 Gateway Timeout
      }
    },
    [
      apiVersion,
      appendMsg,
      botAvatar,
      botData,
      chatStatus,
      contactCollectionOptOn,
      contactEmail,
      contactMobile,
      contactName,
      debouncedPlayFiller,
      dialogId,
      fillers?.length,
      filterPlayed,
      hasTalkingHead,
      idToken,
      idle,
      idleVideoSrc,
      isMobile,
      keepRecording,
      lipsyncVersion,
      params,
      sessionAttr,
      sessionId,
      setTyping,
      stopVoice,
    ]
  );

  const initialCall = useCallback(
    async (initialMsg?: string) => {
      try {
        setAuthLoading(true);
        const response = await authenticate(dialogId);
        if (!response) {
          appendMsg({ type: 'initialError' });
          return;
        }

        const {
          AuthenticationResult,
          bot_alias_id,
          bot_id,
          idle_url,
          image_url,
          lipsync_version,
          locale_id,
          talkingHeadOn,
          voice,
          wakeupWord,
          filler_videos,
          contactCollectionOnStart,
          contactCollectionCardText,
          quickQuestions,
          chatUITheme,
          APIVersion,
          dialog_name,
        } = response;

        setBotData({ bot_alias_id, bot_id, idle_url, locale_id, voice });
        setIdToken(AuthenticationResult.IdToken);
        setWakeupMsg(wakeupWord);
        setApiVersion(APIVersion || '1');
        setTheme(chatUITheme || 'theme-0');

        if (image_url) {
          setBotAvatar(image_url);
        }
        if (filler_videos) {
          setFillers(filler_videos);
        }
        setLipsyncVersion(lipsync_version);

        if (contactCollectionOnStart) {
          setContactCollectionOptOn(contactCollectionOnStart);
          setContactCollectionTitle(
            contactCollectionCardText || DEFAULT_MESSAGE.startWithContact
          );
          if (localStorage) {
            setContactName(localStorage.getItem('diyvaBotContactName') || '');
            setContactEmail(localStorage.getItem('diyvaBotContactEmail') || '');
            setContactMobile(
              localStorage.getItem('diyvaBotContactMobile') || ''
            );
          }
        }

        if (talkingHeadOn) {
          setTalkingHeadOptOn(talkingHeadOn);
        }

        if (isMobile || collapsed) {
          setHasTalkingHead(false);
        } else if (talkingHeadOn) {
          setHasTalkingHead(talkingHeadOn);
        }

        if (idle_url) {
          setIdleVideoSrc(idle_url);
          setVideoSrc(idle_url);
        }

        if (quickQuestions && quickQuestions.length > 0) {
          setQuickQuestionList(
            quickQuestions.map((q: any) => {
              if (typeof q === 'string') {
                return { name: q, msg: q };
              } else {
                return {
                  name: q.short,
                  msg: q.long,
                };
              }
            })
          );
        }

        if (!initialMsg) {
          appendMsg({
            type: 'start',
            position: 'center',
            _id: START_MSG_ID,
          });
          trackEvent('load_bot', {
            dialog_name: dialog_name,
          });
        } else {
          deleteMsg(END_MSG_ID);
          appendMsg({
            type: 'divider',
            content: { text: 'New Session' },
          });
          postMessage(
            'text',
            initialMsg,
            true,
            true,
            AuthenticationResult.IdToken
          );
        }
      } catch (e) {
        console.log(e);
        appendMsg({ type: 'initialError' });
      }
      setInitialLoading(false);
      setAuthLoading(false);
    },
    [appendMsg, collapsed, deleteMsg, isMobile, postMessage, dialogId]
  );

  const startChat = useCallback(async () => {
    if (contactName) {
      trackEvent('fill_form', {
        Field: 'name',
      });
    }
    if (contactEmail) {
      trackEvent('fill_form', {
        Field: 'email',
      });
    }
    if (contactMobile) {
      trackEvent('fill_form', {
        Field: 'mobile',
      });
    }
    trackEvent('click', {
      button: 'Start',
    });

    deleteMsg(START_MSG_ID);
    if (contactCollectionOptOn && localStorage) {
      localStorage.setItem('diyvaBotContactName', contactName);
      localStorage.setItem('diyvaBotContactEmail', contactEmail);
      localStorage.setItem('diyvaBotContactMobile', contactMobile);
    }
    postMessage('text', wakeupMsg, true, true);
  }, [
    contactCollectionOptOn,
    contactEmail,
    contactMobile,
    contactName,
    deleteMsg,
    postMessage,
    wakeupMsg,
  ]);

  const restart = useCallback(() => {
    resetState();
    initialCall(wakeupMsg);
  }, [initialCall, resetState, wakeupMsg]);

  const onPageReload = useCallback(() => {
    window.location.reload();
  }, []);

  const handleFeedback = useCallback(
    async (call_id: string, feedbackValue: string) => {
      await sendFeedback(idToken, call_id, feedbackValue);
    },
    [idToken]
  );

  const handleSend = useCallback(
    async (type: string, val: string, audioUrl?: string) => {
      if (!val) return;
      if (hasTalkingHead) {
        if (isFillerPlaying) {
          setIsFillerPlaying(false);
        }
        setVideoSrc(idleVideoSrc);
        abortFetchResponse();
      }
      appendMsg({
        type: type as MessageType,
        content: type === 'text' ? { text: val } : { audio: audioUrl },
        position: 'right',
        _id: nanoid(),
      });
      postMessage(type, val);
      trackEvent('send_message', {
        message_type: type as 'text' | 'voice',
      });
    },
    [appendMsg, hasTalkingHead, idleVideoSrc, isFillerPlaying, postMessage]
  );

  const multiMediaAction = useCallback(
    (type: string) => {
      switch (type) {
        case 'product':
          return {};
        case 'video':
          return {};
        case 'user_options':
          return {
            onSelect: (option: string) => {
              stopVoice();
              handleSend('text', option);
            },
          };
        case 'image_upload':
          return {
            onSuccess: () => {
              stopVoice();
              handleSend('text', 'Image uploaded');
            },
            onError: () => {
              //TODO: handle error
            },
          };
        default:
          return {};
      }
    },
    [handleSend, stopVoice]
  );

  const renderMessageContent = useCallback(
    (msg: MessageProps) => {
      const { type, content } = msg;
      switch (type as MessageType) {
        case 'text':
          return <Bubble content={content.text} />;
        case 'translation':
          return <TranslationBubble content={content.text} />;
        case 'start':
          if (contactCollectionOptOn) {
            return (
              <ActionCard
                title={contactCollectionTitle}
                actionButtons={[
                  { text: 'Start', color: 'primary', onClick: startChat },
                ]}
              >
                <Input
                  placeholder="name"
                  value={contactName}
                  onChange={setContactName}
                  className="contact-input"
                />
                <Input
                  placeholder="email"
                  value={contactEmail}
                  onChange={setContactEmail}
                  className="contact-input"
                />
                <Input
                  placeholder="mobile"
                  value={contactMobile}
                  onChange={setContactMobile}
                  className="contact-input"
                />
              </ActionCard>
            );
          } else {
            return (
              <ActionCard
                title={DEFAULT_MESSAGE.start}
                actionButtons={[
                  { text: 'Start', color: 'primary', onClick: startChat },
                ]}
              />
            );
          }
        case 'initialError':
          return chatStatus === 'beforeStart' ? <InitialErrorMessage /> : null;
        case 'initialLoading':
          return initialLoading ? <InitialLoadingMessage /> : null;
        case 'voice':
          return (
            <Bubble style={{ padding: '0.5rem' }}>
              <AudioMessage
                messageId={msg._id}
                src={content.audio}
                audioPlayingId={currentAudioPlayingId}
                onPlay={() => setCurrentAudioPlayingId(msg._id)}
              />
            </Bubble>
          );
        case 'responseError':
          return <Bubble content={content.text}></Bubble>;
        case 'authError':
          return (
            <ActionCard
              title={content.text}
              actionButtons={[
                { text: 'Restart', color: 'primary', onClick: onPageReload },
              ]}
            />
          );
        case 'micPermissionDeniedError':
          return (
            <ActionCard
              title={content.text}
              image={mic}
              titleIcon="info-circle"
            />
          );
        case 'micPermissionDismissedError':
          return (
            <ActionCard
              title={content.text}
              titleIcon="info-circle"
              image={allowmic}
            />
          );
        case 'recorderError':
          return <ActionCard title={content.text} titleIcon="info-circle" />;
        case 'audioSupportError':
          return <ActionCard title={content.text} titleIcon="info-circle" />;
        case 'end':
          let button = {
            text: 'Restart',
            color: 'primary',
            onClick: restart,
          } as ButtonProps;

          if (authLoading) {
            button = {
              ...button,
              disabled: true,
              icon: <Icon type="spinner" className="btn_icon" spin />,
            };
          }
          return (
            <ActionCard
              title={content.title}
              description={content.text}
              actionButtons={[button]}
            />
          );
        case 'divider':
          return <Divider>{content.text}</Divider>;
        case 'response':
          const actions = content.multiMediaResponse
            ? multiMediaAction(content.multiMediaResponse.type)
            : [];
          return (
            <ResponseContainer
              call_id={content.call_id}
              handleFeedback={handleFeedback}
            >
              {content.text && (
                <TextMessage
                  messageId={msg._id}
                  text={content.text}
                  audio={content.audio}
                  audioPlayingId={currentAudioPlayingId}
                  onPlay={() => setCurrentAudioPlayingId(msg._id)}
                />
              )}
              {content.multiMediaResponse && (
                <MultimediaResponseCard
                  multiMediaResponse={content.multiMediaResponse}
                  actions={actions}
                />
              )}
            </ResponseContainer>
          );
        case 'test':
          return (
            <ActionCard
              title={content.title}
              description={content.text}
              actionButtons={content.buttons}
            />
          );
        default:
          return null;
      }
    },
    [
      authLoading,
      chatStatus,
      contactCollectionOptOn,
      contactCollectionTitle,
      contactEmail,
      contactMobile,
      contactName,
      currentAudioPlayingId,
      handleFeedback,
      initialLoading,
      onPageReload,
      restart,
      startChat,
      multiMediaAction,
    ]
  );

  const startVoice = useCallback(async () => {
    if (hasTalkingHead) {
      if (isFillerPlaying) {
        setIsFillerPlaying(false);
      }
      setVideoSrc(idleVideoSrc);
      setChatStatus('idle');
      abortFetchResponse();
    } else {
      // audioRef.current?.pause();
      setAudioSrc('');
    }

    setInputType('voice');
    recordVoice();
  }, [hasTalkingHead, idleVideoSrc, isFillerPlaying, recordVoice]);

  const onVideoEnded = useCallback(() => {
    if (chatStatus !== 'responding' && !isFillerPlaying) return;
    if (isFillerPlaying) {
      setIsFillerPlaying(false);
      setVideoSrc(idleVideoSrc);
      return;
    }

    idle();
    keepRecording();
  }, [chatStatus, isFillerPlaying, idle, keepRecording, idleVideoSrc]);

  const onAudioEnded = useCallback(() => {
    if (chatStatus !== 'responding') return;
    idle();
    keepRecording();
  }, [chatStatus, idle, keepRecording]);

  const sendVoice = useCallback(() => {
    const callback = (blob: any, audioUrl: string) => {
      handleSend('voice', blob, audioUrl);
    };
    stopRecording(callback);
  }, [handleSend, stopRecording]);

  useEffect(() => {
    if (silenceDetected && recorder?.state === 'recording') {
      sendVoice();
    }
  }, [recorder?.state, sendVoice, silenceDetected]);

  const scrollMessageList = useCallback(() => {
    const el = document.getElementsByClassName('PullToRefresh')[0];
    el.scrollTo(0, el.scrollHeight);
  }, []);

  const talkingHeadToggleMute = useCallback(() => {
    setTalkingHeadMuted(!talkingHeadMuted);
  }, [talkingHeadMuted]);

  const closeVoiceComposer = useCallback(() => {
    if (recorder && recorder.state === 'recording') {
      recorder.stop();
    }
    setInputType('text');
  }, [recorder]);

  useEffect(() => {
    appendMsg({ type: 'initialLoading' });
    initialCall();
  }, [appendMsg]);

  return (
    <div className={`${theme} bot-container${isMobile ? ' mobile' : ''}`}>
      {hasTalkingHead && (
        <div className="talking-head">
          {initialLoading ? (
            <GridLoader />
          ) : (
            <TalkingHead
              playerRef={playerRef}
              videoSrc={videoSrc}
              isIdle={chatStatus !== 'responding' && !isFillerPlaying}
              onEnded={onVideoEnded}
              background={botAvatar}
              idleVideo={idleVideoSrc}
              muted={talkingHeadMuted}
              toggleMute={talkingHeadToggleMute}
            />
          )}
        </div>
      )}

      <div className="chat-box">
        <Chat
          messages={messages}
          renderMessageContent={renderMessageContent}
          onSend={handleSend}
          locale="en-US"
          inputType={inputType}
          placeholder={DEFAULT_MESSAGE.inputPlaceholder}
          rightAction={{
            icon: 'mic',
            onClick: startVoice,
            color: 'primary',
            variant: 'outline',
            className: 'record-btn',
            disabled: chatStatus === 'beforeStart' || chatStatus === 'end',
          }}
          inputOptions={{
            disabled: chatStatus === 'beforeStart' || chatStatus === 'end',
          }}
          onInputFocus={scrollMessageList}
          quickReplies={
            chatStatus !== 'beforeStart' && chatStatus !== 'end'
              ? quickQuestionList
              : []
          }
          onQuickReplyClick={(_, index) => {
            stopVoice();
            handleSend('text', quickQuestionList[index].msg);
            trackEvent('click', {
              button: 'QuickReply',
            });
          }}
        />
        {inputType === 'voice' && (
          <VoiceComposer
            chatStatus={chatStatus}
            onStopRecording={stopVoice}
            onStopVoiceMode={closeVoiceComposer}
          />
        )}
        <div className="diyva-footer">
          <a
            target="_blank"
            href={`https://diyva.life?utm_source=chatbot&utm_medium=footerlink&utm_id=${dialogId}`}
            rel="noreferrer"
            onClick={() => {
              trackEvent('click', {
                button: 'PoweredByDiyva',
              });
            }}
          >
            Powered by <strong>Diyva</strong>
          </a>
        </div>
        <audio
          src={audioSrc}
          onEnded={onAudioEnded}
          ref={audioRef}
          autoPlay
        ></audio>
      </div>
    </div>
  );
};

export default BotContainer;
